// Ported from dev-scrips-pm-react src/billing/components/package-redemptions-note.tsx (PROD-3312 appointment Billing tab). PM primitives come from the
// PROD-3310 billing bridge (`@/billing/bridge`); PM's arbitrary-value classes are the named utilities on the same tokens.
import { BadgeCheck, Ticket } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useInvoicePackageRedemptions } from '@/billing/services/use-package-grants';
import { formatMoney } from '@/shared/lib/money';
import type { PackageRedemption } from '@/shared/types/billing';

/**
 * What a prepaid entitlement settled on this invoice (PROD-2126, PROD-2152).
 * Renders nothing for drafts and for invoices with no standing redemption; the
 * amounts are already reflected in Patient Payable, this line says why.
 *
 * Own `<section>`, sibling of (and immediately above) the invoice's Payments
 * section: a redemption is not a receipt, so it must not hang off the Payments
 * guard (doing so forced that guard to widen and rendered a bare "Payments"
 * heading on non-draft invoices with no receipts at all). The section is
 * rendered INSIDE this component so an invoice with no redemptions contributes
 * no element whatsoever: the host body is `space-y-6`, where an always-present
 * empty wrapper would still claim a 24px gap.
 *
 * **One strip per settlement source.** A membership allowance and a package
 * grant settle through the same ledger, and every row says which one paid
 * (`settlementSource`). Until PROD-2152 this component ignored that field and
 * called every settlement "Paid by package", so an invoice covered by the
 * patient's membership told them a package had paid for it. One patient can
 * hold both over the same service - `PackageRedemptionService` walks both
 * kinds of target in a single FEFO pass - so the rows are grouped rather than
 * classified by the first one: an invoice where a pack covered one line and
 * the membership another shows both, each with its own units and amount.
 */
export interface PackageRedemptionsNoteProps {
  invoiceId: string | undefined;
  /** False for drafts - nothing is consumed until the invoice is issued. */
  enabled: boolean;
  currency?: string;
}

/**
 * The two icons are the ones the patient's receipts list already uses for the
 * same two ideas (`patient-advance-receipts-list`): a ticket is a package, a
 * badge is a membership. Same concept, same glyph, on both screens where a
 * patient's entitlements appear.
 *
 * Fixed order, so an invoice settled by both does not reorder its own strips
 * between renders as the ledger returns rows in FEFO order.
 */
const SOURCES: ReadonlyArray<{ key: string; label: string; Icon: LucideIcon }> = [
  { key: 'PackageGrant', label: 'Paid by package', Icon: Ticket },
  { key: 'Membership', label: 'Covered by membership', Icon: BadgeCheck },
];

/**
 * A settlement source this build does not know about. `SettlementSourceEnum`
 * is documented as taking a third value rather than growing a second ledger,
 * so an older UI against a newer API is a real state, not a theoretical one.
 * Say only what is certainly true - something prepaid covered it. Naming the
 * wrong instrument is the bug this replaced, and guessing "package" again for
 * an unrecognised value would reintroduce it.
 */
const UNKNOWN_SOURCE = { key: 'Unknown', label: 'Covered', Icon: BadgeCheck } as const;

function groupBySource(rows: PackageRedemption[]) {
  const groups = SOURCES.map((source) => ({
    ...source,
    rows: rows.filter((r) => r.settlementSource === source.key),
  })).filter((group) => group.rows.length > 0);

  const unrecognised = rows.filter(
    (r) => !SOURCES.some((source) => source.key === r.settlementSource),
  );

  return unrecognised.length > 0
    ? [...groups, { ...UNKNOWN_SOURCE, rows: unrecognised }]
    : groups;
}

export function PackageRedemptionsNote({
  invoiceId,
  enabled,
  currency = 'AED',
}: PackageRedemptionsNoteProps) {
  const { data } = useInvoicePackageRedemptions(invoiceId, enabled);
  const rows = data ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="space-y-3">
      {groupBySource(rows).map(({ key, label, Icon, rows: settled }) => {
        const units = settled.reduce((sum, r) => sum + r.unitsConsumed, 0);
        const amount = settled.reduce((sum, r) => sum + r.amount, 0);
        return (
          <div
            key={key}
            className="flex items-center justify-between rounded-md border border-border-default bg-blue-100/40 px-3 py-2 text-sm"
          >
            <span className="flex items-center gap-2 text-text-secondary">
              <Icon className="h-4 w-4 text-brand-primary" aria-hidden />
              {label} · {units} {units === 1 ? 'session' : 'sessions'}
            </span>
            <span className="font-semibold text-brand-depth tabular-nums">
              {currency} {formatMoney(amount)}
            </span>
          </div>
        );
      })}
    </section>
  );
}
